import { makeAutoObservable } from "mobx";
import config from "../config";
import { APP_PATHS } from "../constants/app-paths";
import pageUrlCoordinationStore from "../components/layouts/app-layout/page-url-broadcaster/page-url-coordination-store";
import rmsMixpanelStore from "./rms-mixpanel-store";

/**
 * Page view tracking store for Mixpanel analytics.
 * Handles extracting route information and tracking page views with correct browser URLs.
 */
class PageViewTrackerStore {
    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /**
     * Tracks a Mixpanel page view for the current route.
     * Handles RMA ID extraction from path and constructs the full browser URL with Laravel path mapping.
     * @param pathname - Current React Router pathname
     * @param searchParams - Current React Router search params
     */
    public trackCurrentPageView(pathname: string, searchParams: URLSearchParams): void {
        let adjustedSearchParams = searchParams;

        const rmaIdFromPath = this._extractRmaIdFromPath(pathname);
        if (rmaIdFromPath && !searchParams.has('rmaid')) {
            /**
             * When navigating within the SPA to an RMA (e.g. /manage-returns/rma-list-page/139),
             * React Router does not add `rmaid` to the query string. The `rmaid` is only in the pathname.
             * For Mixpanel page view tracking we need the full browser URL including `rmaid`.
             * We extract the RMA ID from the path and merge it into search params so the tracked
             * URL Path includes ?rmaid=139.
             */
            const merged = new URLSearchParams(searchParams);
            merged.set('rmaid', rmaIdFromPath);
            adjustedSearchParams = merged;
        }

        const laravelPath = pageUrlCoordinationStore.constructLaravelUrl(pathname);
        const searchString = this._buildFilteredSearchString(adjustedSearchParams);
        const pathWithQuery = searchString ? `${laravelPath}?${searchString}` : laravelPath;
        const fullBrowserUrl = `${config.apiUrl}${pathWithQuery}`;

        rmsMixpanelStore.trackPageView(pathWithQuery, fullBrowserUrl, { 'RMS Path': pathname });
    }

	/**
	 * Extracts RMA ID from the returns RMA detail path (e.g. /manage-returns/rma-list-page/139 -> "139").
	 * Uses pattern matching to detect the returns route with an RMA ID segment.
	 * Returns null if the path is not the returns RMA detail page.
	 */
	private _extractRmaIdFromPath(pathname: string): string | null {
		const bases = [APP_PATHS.RETURNS_NEW, APP_PATHS.RETURNS];
		for (const returnsBasePath of bases) {
			const regExpPattern = new RegExp(`^${returnsBasePath}/(?<rmaId>[^/]+)`);
			const match = regExpPattern.exec(pathname)?.groups;
			if (match?.rmaId) {
				return match.rmaId;
			}
		}
		return null;
	}

    /**
     * Filters out internal params (uuid, rms-version) and converts to query string.
     */
    private _buildFilteredSearchString(searchParams: URLSearchParams): string {
        const paramsToExclude = ['uuid', 'rms-version'];
        const filtered = new URLSearchParams();
        
        for (const [key, value] of searchParams) {
            if (!paramsToExclude.includes(key)) {
                filtered.append(key, value);
            }
        }
        
        return filtered.toString();
    }
}

const pageViewTrackerStore = new PageViewTrackerStore();
export default pageViewTrackerStore;
